import axios from "axios";
import type { Query, QueryClient } from "@tanstack/react-query";
import { scopedKey, type QueryScope } from "./scope";

/**
 * What a confirmed write does to the cache (contract §3). Each writer names,
 * per key, whether it **invalidates** (refetch on the next observe, or now if
 * observed), **patches** (writes the response into an entry that already
 * exists, no request), or leaves it alone. Every helper here takes a scope, so
 * a writer can only reach the signed-in principal's entries in one workspace.
 */

/**
 * Invalidate every entry under `[tenant, principal, domain, …prefix]`. Prefix
 * match: `("project", id)` covers the detail and all its sub-resources. Settles
 * when the observed ones have refetched; a failed refetch does not reject it
 * (the cache logs it, `client.ts`).
 */
export function invalidateScoped(
  client: QueryClient,
  scope: QueryScope,
  domain: string,
  ...prefix: readonly unknown[]
): Promise<void> {
  return client.invalidateQueries({ queryKey: scopedKey(scope, domain, ...prefix) });
}

/**
 * Write a response into the one entry at `key` if it is already cached. An
 * absent entry stays absent: a write's response is not the read's full
 * representation, so it must not seed a fresh-looking entry.
 */
export function patchScoped<T>(
  client: QueryClient,
  key: readonly unknown[],
  update: (current: T) => T,
): void {
  client.setQueryData<T>(key, (current) => (current === undefined ? undefined : update(current)));
}

/**
 * After a confirmed write to the list at `key`: cancel a read of it still in
 * flight, whose payload predates the write and would otherwise land on top of
 * it, then patch the cached list with `update`. If nothing was cached (a first
 * read was what got cancelled), the list is re-read instead, so its observers
 * are not left without data. Each key in `stale` is marked stale too. Never
 * rejects: a failed re-read is the cache's to log, and a failed mark is
 * logged here under `owner`.
 */
export async function patchAfterWrite<T>(
  client: QueryClient,
  key: readonly unknown[],
  update: (current: T) => T,
  { stale = [], owner }: { stale?: readonly (readonly unknown[])[]; owner: string },
): Promise<void> {
  await client.cancelQueries({ queryKey: key, exact: true });
  const cached = client.getQueryData(key) !== undefined;
  if (cached) patchScoped(client, key, update);
  for (const queryKey of cached ? stale : [key, ...stale]) {
    client.invalidateQueries({ queryKey, exact: true }).catch((error: unknown) => {
      console.warn(`[${owner}] marking stale after a write failed`, { queryKey, error });
    });
  }
}

/**
 * Start a re-read after a confirmed write without holding the write on it, for
 * a writer whose result is already on screen (optimistic, or patched from the
 * server's answer). TanStack awaits a promise returned from a mutation's
 * `onSuccess`, `onError` or `onSettled` before the mutation settles, so a
 * returned re-read keeps the write pending (`isPending`, `mutateAsync`,
 * `useMutationState`) until the list comes back: on a slow server a created
 * status was listed, its Edit live, beside an add form still open and disabled
 * (NG-3132). A writer whose result only the re-read shows returns the re-read
 * instead, so its controls stay busy until the result is on screen. A failed
 * re-read is logged under `owner`.
 */
export function rereadWithoutWaiting(
  reread: Promise<unknown>,
  owner: string,
  context: Record<string, unknown> = {},
): void {
  reread.catch((error: unknown) => {
    console.warn(`[${owner}] re-reading after a write failed`, { ...context, error });
  });
}

/**
 * After a confirmed write: re-read the entries `predicate` names, each read
 * starting after the write. Any read still in flight is cancelled, every entry is
 * marked stale at once (so a caller sees that the moment its write settles), and
 * the observed ones read again.
 *
 * The cancel is the point. `invalidateQueries` cancels a read in flight only when
 * its entry already has data, and joins a first read instead, so that read's
 * answer, taken before the write, would stand: a deleted card stayed on a Board
 * opened just before the delete (NG-2135). It runs before the mark because a
 * cancel puts the entry back as it was when the read began, unmarked. The
 * predicate must stay inside one scope's entries, as the scoped helpers above do
 * by construction.
 */
export async function rereadAfterWrite(
  client: QueryClient,
  predicate: (query: Query) => boolean,
): Promise<void> {
  await Promise.all([
    client.cancelQueries({ predicate, fetchStatus: "fetching" }),
    client.invalidateQueries({ predicate, refetchType: "none" }),
  ]);
  await client.refetchQueries({ predicate, type: "active" });
}

/**
 * On a resource's 403 or 404, remove everything cached under the prefix so a
 * later mount cannot render the old answer, and report whether it did. Any
 * other error leaves the cache alone. This is the resource's own denial; a
 * dead session is `syncQueryPrincipal`'s job.
 */
export function dropOnDenial(
  client: QueryClient,
  scope: QueryScope,
  error: unknown,
  domain: string,
  ...prefix: readonly unknown[]
): boolean {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  if (status !== 403 && status !== 404) return false;
  client.removeQueries({ queryKey: scopedKey(scope, domain, ...prefix) });
  return true;
}
